import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { Country } from "@omni/types/enums/country.enum";
import { head } from "@omni/utils/array.utils";

import { ExternalAPI } from "@/api/api";
import { Config } from "@/utils/config.utils";
import { BPOSTErrorSeverity } from "@/utils/enums";

export type ErrorField = {
    key: string;
    value: any;
};

export type BPostAddressValidationResult = {
    isTimeout?: boolean;
    isValid: boolean;
    newStatus?: AddressValidationStatus;
    officialAddress?: {
        streetName: string;
        number: string;
        boxNumber?: string;
        zipCode: string;
        city: string;
        address: string;
        country: string;
        line: string;
    };
    errorFields?: Array<ErrorField>;
};

type ValidatedAddress = {
    PostalAddress: {
        StructuredDeliveryPointLocation: {
            StreetName: string;
            StreetNumber: string;
            BoxNumber?: string;
        };
        StructuredPostalCodeMunicipality: {
            PostalCode: string;
            MunicipalityName: string;
        };
        CountryName: string;
    };
    Label: {
        Line: Array<string>;
    };
};

type ValidationError = {
    ComponentRef: string;
    ErrorCode: string;
    ErrorSeverity: BPOSTErrorSeverity;
};

type ValidatedAddresses = {
    "@id": string;
    ValidatedAddressList: {
        ValidatedAddress?: Array<ValidatedAddress>;
    };
    Error: Array<ValidationError>;
};

export type AddressValidationParams = {
    street: string;
    number: string;
    zipCode: string;
    city: string;
    box?: string | null;
};

export type AutocompleteAddress = {
    string: string;
    searchBarString: string;
    houseNumber: string;
    streetName: string;
    municipalityName: string;
    postalCode: string;
    boxNumber?: string;
    listOfBoxes?: Array<string>;
    numberOfBoxes?: number;
    isComplete?: boolean;
};

type AutocompleteResult = {
    isTimeout: boolean;
    topSuggestions: Array<{ address: AutocompleteAddress }>;
};

const BPOST_TIMEOUT = 5000;

const mapErrorFields: {
    [key: string]: string;
} = {
    StreetName: "street",
    StreetNumber: "number",
    PostalCode: "zipCode",
    MunicipalityName: "city",
    BoxNumber: "box"
};

const AddressNotRecognizedErrorCode = "address_not_recognized";
export default class BPostService {
    static validateAddress(
        params: AddressValidationParams
    ): Promise<BPostAddressValidationResult> {
        return new Promise<BPostAddressValidationResult>((resolve) => {
            const { street, number, zipCode, city, box = "" } = params;
            const addressObj: {
                [key: string]: any;
            } = {
                street,
                number,
                zipCode,
                city,
                box
            };

            const validationEndpoint =
                Config.integrations["bpost-url-validation"];
            if (!validationEndpoint) {
                return resolve({
                    isValid: true,
                    newStatus: AddressValidationStatus.Unknown
                });
            }

            ExternalAPI.post(
                validationEndpoint,
                {
                    ValidateAddressesRequest: {
                        AddressToValidateList: {
                            AddressToValidate: [
                                {
                                    "@id": "1",
                                    PostalAddress: {
                                        DeliveryPointLocation: {
                                            StructuredDeliveryPointLocation: {
                                                StreetName: street,
                                                StreetNumber: number,
                                                BoxNumber: box || ""
                                            }
                                        },
                                        PostalCodeMunicipality: {
                                            StructuredPostalCodeMunicipality: {
                                                PostalCode: zipCode,
                                                MunicipalityName: city
                                            }
                                        }
                                    }
                                }
                            ]
                        },
                        ValidateAddressOptions: {
                            IncludeFormatting: true,
                            IncludeSuggestions: false,
                            IncludeSubmittedAddress: false
                        },
                        CallerIdentification: {
                            CallerName: "Hubo"
                        }
                    }
                },
                {
                    timeout: BPOST_TIMEOUT,
                    headers: {
                        "x-api-key": Config.integrations["bpost-api-key"]
                    }
                }
            )
                .then((response) => {
                    const validatedAddressResult: Array<ValidatedAddresses> =
                        response.data.ValidateAddressesResponse
                            .ValidatedAddressResultList.ValidatedAddressResult;

                    const validatedAddress = validatedAddressResult.find(
                        (address) => address["@id"] === "1"
                    );

                    const validationErrors = (
                        validatedAddress?.Error || []
                    ).filter(
                        (e) => e.ErrorSeverity === BPOSTErrorSeverity.Error
                    );

                    const firstError = head(validationErrors);

                    if (
                        !validatedAddress ||
                        (validationErrors.length === 1 &&
                            firstError?.ErrorCode ===
                                AddressNotRecognizedErrorCode)
                    ) {
                        return resolve({
                            isValid: false,
                            errorFields: Object.keys(addressObj).map((k) => ({
                                key: k,
                                value: addressObj[k]
                            }))
                        });
                    }

                    if (validationErrors.length > 0) {
                        return resolve({
                            isValid: false,
                            errorFields: validationErrors
                                .filter((e) => !!e.ComponentRef)
                                .map((e) => ({
                                    key: mapErrorFields[e.ComponentRef],
                                    value: addressObj[
                                        mapErrorFields[e.ComponentRef]
                                    ]
                                }))
                        });
                    }

                    // Als er geen errors zijn hierboven is er sowieso validatie gebeurt
                    const address =
                        validatedAddress?.ValidatedAddressList
                            ?.ValidatedAddress?.[0]!;

                    resolve({
                        isTimeout: false,
                        isValid: true,
                        newStatus: AddressValidationStatus.Validated,
                        officialAddress: {
                            streetName:
                                address.PostalAddress
                                    .StructuredDeliveryPointLocation.StreetName,
                            number: address.PostalAddress
                                .StructuredDeliveryPointLocation.StreetNumber,
                            boxNumber:
                                address.PostalAddress
                                    .StructuredDeliveryPointLocation.BoxNumber,
                            zipCode:
                                address.PostalAddress
                                    .StructuredPostalCodeMunicipality
                                    .PostalCode,
                            city: address.PostalAddress
                                .StructuredPostalCodeMunicipality
                                .MunicipalityName,
                            address: address.Label.Line.join(" - "),
                            country: Country.Be,
                            line: address.Label.Line.join(" ")
                        }
                    });
                })
                .catch(() => {
                    resolve({
                        isValid: true,
                        newStatus: AddressValidationStatus.Unknown
                    });
                });
        });
    }

    static autocomplete(query: string): Promise<AutocompleteResult> {
        return new Promise<AutocompleteResult>((resolve) => {
            const autocompleteEndpoint =
                Config.integrations["bpost-url-autocomplete"];
            if (!autocompleteEndpoint) {
                return resolve({ isTimeout: false, topSuggestions: [] });
            }

            ExternalAPI.get(autocompleteEndpoint, {
                params: {
                    IncludeListOfBoxes: true,
                    q: query
                },
                headers: {
                    "x-api-key": Config.integrations["bpost-api-key"]
                },
                timeout: BPOST_TIMEOUT
            })
                .then((response) => {
                    resolve({
                        isTimeout: false,
                        topSuggestions: response.data.response.topSuggestions
                    });
                })
                .catch((e) =>
                    resolve({
                        isTimeout: e.code === "ECONNABORTED",
                        topSuggestions: []
                    })
                );
        });
    }
}
